<template>
  <div class="scheduled-task-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>定时任务管理</span>
          <div>
            <el-button type="success" @click="handleRefreshAll">刷新所有任务</el-button>
            <el-button type="primary" @click="handleCreate">新增任务</el-button>
          </div>
        </div>
      </template>

      <el-alert type="info" show-icon :closable="false" class="scheduled-task-tip">
        <template #title>与企业公海策略联动</template>
        内置任务 <code>CRM_PUBLIC_SEA_AUTO_RECYCLE</code>（若已执行库脚本）按 Cron 调用
        <code>CustomerCrmMaintenanceJob.executePublicSeaAutoRecycle</code>，扫描时读取<strong>每个企业</strong>在「企业管理 → 公海配置」中保存的自建/认领未跟进天数。
        修改 Cron 或禁用任务后，请点击「刷新所有任务」或重启后台使调度生效。
      </el-alert>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="任务编码">
          <el-input
            v-model="queryParams.taskCode"
            placeholder="请输入任务编码"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="任务名称">
          <el-input
            v-model="queryParams.taskName"
            placeholder="请输入任务名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 任务列表 -->
      <el-table
        v-loading="loading"
        :data="taskList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="taskCode" label="任务编码" width="200" />
        <el-table-column prop="taskName" label="任务名称" width="200" />
        <el-table-column prop="taskClass" label="执行类" width="300" show-overflow-tooltip />
        <el-table-column prop="taskMethod" label="执行方法" width="120" />
        <el-table-column prop="cronExpression" label="Cron表达式" width="150" />
        <el-table-column prop="taskDescription" label="任务描述" min-width="200" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="lastExecuteTime" label="最后执行时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.lastExecuteTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="nextExecuteTime" label="下次执行时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.nextExecuteTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="executeCount" label="执行次数" width="100" />
        <el-table-column label="操作" width="320" fixed="right">
          <template #default="{ row }">
            <el-button type="info" size="small" @click="handleViewLogs(row)">查看日志</el-button>
            <el-button type="success" size="small" @click="handleExecuteOnce(row)" :loading="row.executing">
              执行一次
            </el-button>
            <el-button 
              v-if="row.status === 0" 
              type="success" 
              size="small" 
              @click="handleEnable(row)"
            >
              启用
            </el-button>
            <el-button 
              v-if="row.status === 1" 
              type="warning" 
              size="small" 
              @click="handleDisable(row)"
            >
              禁用
            </el-button>
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.current"
          v-model:page-size="queryParams.size"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 查看执行日志对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      title="任务执行日志"
      width="1000px"
      @close="handleLogDialogClose"
    >
      <div class="log-dialog-content">
        <div class="log-task-info">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="任务编码">{{ currentTask?.taskCode }}</el-descriptions-item>
            <el-descriptions-item label="任务名称">{{ currentTask?.taskName }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 日志搜索表单 -->
        <el-form :inline="true" :model="logQueryParams" class="log-search-form">
          <el-form-item label="执行状态">
            <el-select v-model="logQueryParams.executeStatus" placeholder="全部" clearable style="width: 120px">
              <el-option label="成功" :value="1" />
              <el-option label="失败" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item label="开始时间">
            <el-date-picker
              v-model="logQueryParams.startTime"
              type="datetime"
              placeholder="选择开始时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 200px"
            />
          </el-form-item>
          <el-form-item label="结束时间">
            <el-date-picker
              v-model="logQueryParams.endTime"
              type="datetime"
              placeholder="选择结束时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 200px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleLogSearch">查询</el-button>
            <el-button @click="handleLogReset">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 日志列表 -->
        <el-table
          v-loading="logLoading"
          :data="logList"
          border
          style="width: 100%"
          max-height="400"
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="executeStatusText" label="执行状态" width="100">
            <template #default="{ row }">
              <el-tag :type="row.executeStatus === 1 ? 'success' : 'danger'">
                {{ row.executeStatusText }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="executeTime" label="执行时间" width="180">
            <template #default="{ row }">
              {{ formatDateTime(row.executeTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="duration" label="耗时(ms)" width="120">
            <template #default="{ row }">
              {{ row.duration || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="errorMessage" label="错误信息" min-width="200" show-overflow-tooltip>
            <template #default="{ row }">
              <span v-if="row.errorMessage" style="color: #f56c6c;">{{ row.errorMessage }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button 
                v-if="row.errorStack" 
                type="text" 
                size="small" 
                @click="handleViewErrorStack(row)"
              >
                查看堆栈
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 日志分页 -->
        <div class="log-pagination">
          <el-pagination
            v-model:current-page="logQueryParams.current"
            v-model:page-size="logQueryParams.size"
            :total="logTotal"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleLogSizeChange"
            @current-change="handleLogPageChange"
          />
        </div>
      </div>
    </el-dialog>

    <!-- 错误堆栈对话框 -->
    <el-dialog
      v-model="errorStackDialogVisible"
      title="错误堆栈信息"
      width="800px"
    >
      <el-input
        v-model="errorStackContent"
        type="textarea"
        :rows="15"
        readonly
        style="font-family: monospace; font-size: 12px;"
      />
      <template #footer>
        <el-button @click="errorStackDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 创建/编辑任务对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="700px"
      @close="handleDialogClose"
    >
      <el-form
        ref="taskFormRef"
        :model="taskForm"
        :rules="taskFormRules"
        label-width="120px"
      >
        <div v-if="!taskForm.id" class="task-preset-row">
          <el-button type="primary" link @click="fillCrmPublicSeaRecyclePreset">
            填入「企业公海自动回收」推荐配置
          </el-button>
          <span class="task-preset-hint">（任务编码 CRM_PUBLIC_SEA_AUTO_RECYCLE；若已存在请勿重复创建）</span>
        </div>
        <el-form-item label="任务编码" prop="taskCode">
          <el-input v-model="taskForm.taskCode" :disabled="!!taskForm.id" />
          <div class="form-tip">唯一标识，创建后不可修改</div>
        </el-form-item>
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="taskForm.taskName" />
        </el-form-item>
        <el-form-item label="执行类" prop="taskClass">
          <el-input v-model="taskForm.taskClass" placeholder="如：com.zx.job.OperationLogCleanupJobHandler" />
          <div class="form-tip">任务执行类的全限定名</div>
        </el-form-item>
        <el-form-item label="执行方法" prop="taskMethod">
          <el-input v-model="taskForm.taskMethod" placeholder="如：execute" />
          <div class="form-tip">任务执行方法名（无参数）</div>
        </el-form-item>
        <el-form-item label="Cron表达式" prop="cronExpression">
          <el-input v-model="taskForm.cronExpression" placeholder="如：0 0 3 * * ?" />
          <div class="form-tip">
            格式：秒 分 时 日 月 周<br/>
            示例：0 0 3 * * ? (每天凌晨3点)
          </div>
        </el-form-item>
        <el-form-item label="任务描述" prop="taskDescription">
          <el-input 
            v-model="taskForm.taskDescription" 
            type="textarea" 
            :rows="3"
            placeholder="请输入任务描述"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="taskForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getScheduledTaskList, 
  createScheduledTask, 
  updateScheduledTask, 
  deleteScheduledTask,
  enableScheduledTask,
  disableScheduledTask,
  executeScheduledTaskOnce,
  refreshAllScheduledTasks,
  getScheduledTaskLogList
} from '../api/scheduledTask'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'ScheduledTaskManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      queryParams: {
        taskCode: '',
        taskName: '',
        status: null,
        current: 1,
        size: 20
      },
      taskList: [],
      total: 0,
      dialogVisible: false,
      dialogTitle: '新增任务',
      taskForm: {
        id: null,
        taskCode: '',
        taskName: '',
        taskClass: '',
        taskMethod: '',
        cronExpression: '',
        taskDescription: '',
        status: 1
      },
      taskFormRules: {
        taskCode: [
          { required: true, message: '请输入任务编码', trigger: 'blur' }
        ],
        taskName: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        taskClass: [
          { required: true, message: '请输入执行类', trigger: 'blur' }
        ],
        taskMethod: [
          { required: true, message: '请输入执行方法', trigger: 'blur' }
        ],
        cronExpression: [
          { required: true, message: '请输入Cron表达式', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },
      // 日志相关
      logDialogVisible: false,
      logLoading: false,
      currentTask: null,
      logList: [],
      logTotal: 0,
      logQueryParams: {
        executeStatus: null,
        startTime: null,
        endTime: null,
        current: 1,
        size: 20
      },
      // 错误堆栈对话框
      errorStackDialogVisible: false,
      errorStackContent: ''
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const res = await getScheduledTaskList(this.queryParams)
        if (res.code === '000000') {
          this.taskList = res.data.records || []
          this.total = res.data.total || 0
          // 为每个任务添加执行状态标记（Vue 3 直接赋值即可）
          this.taskList.forEach(task => {
            task.executing = false
          })
        } else {
          ElMessage.error(res.msg || '加载数据失败')
        }
      } catch (error) {
        ElMessage.error('加载数据失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    // 搜索
    handleSearch() {
      this.queryParams.current = 1
      this.loadData()
    },
    // 重置
    handleReset() {
      this.queryParams = {
        taskCode: '',
        taskName: '',
        status: null,
        current: 1,
        size: 20
      }
      this.loadData()
    },
    // 分页大小改变
    handleSizeChange(size) {
      this.queryParams.size = size
      this.queryParams.current = 1
      this.loadData()
    },
    // 页码改变
    handlePageChange(page) {
      this.queryParams.current = page
      this.loadData()
    },
    fillCrmPublicSeaRecyclePreset() {
      this.taskForm.taskCode = 'CRM_PUBLIC_SEA_AUTO_RECYCLE'
      this.taskForm.taskName = '企业公海自动回收（按企业自建/认领天数）'
      this.taskForm.taskClass = 'com.zx.job.CustomerCrmMaintenanceJob'
      this.taskForm.taskMethod = 'executePublicSeaAutoRecycle'
      this.taskForm.cronExpression = '0 30 8 * * ?'
      this.taskForm.taskDescription =
        '扫描各启用企业的私海客户：按 company.public_sea_recycle_days（自建/导入）与 public_sea_recycle_days_claimed（公海认领）及 ownership_origin 区分；超未跟进天数且无已通过合同/成交台账则退回公海。'
      this.taskForm.status = 1
      ElMessage.success('已填入推荐配置，请确认任务编码未被占用后再保存')
    },
    // 创建
    handleCreate() {
      this.dialogTitle = '新增任务'
      this.taskForm = {
        id: null,
        taskCode: '',
        taskName: '',
        taskClass: '',
        taskMethod: '',
        cronExpression: '',
        taskDescription: '',
        status: 1
      }
      this.dialogVisible = true
    },
    // 编辑
    handleEdit(row) {
      this.dialogTitle = '编辑任务'
      this.taskForm = {
        id: row.id,
        taskCode: row.taskCode,
        taskName: row.taskName,
        taskClass: row.taskClass,
        taskMethod: row.taskMethod,
        cronExpression: row.cronExpression,
        taskDescription: row.taskDescription || '',
        status: row.status
      }
      this.dialogVisible = true
    },
    // 删除
    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除任务"${row.taskName}"吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        const res = await deleteScheduledTask(row.id)
        if (res.code === '000000') {
          ElMessage.success('删除成功')
          this.loadData()
        } else {
          ElMessage.error(res.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败：' + error.message)
        }
      }
    },
    // 启用
    async handleEnable(row) {
      try {
        const res = await enableScheduledTask(row.id)
        if (res.code === '000000') {
          ElMessage.success('启用成功')
          this.loadData()
        } else {
          ElMessage.error(res.msg || '启用失败')
        }
      } catch (error) {
        ElMessage.error('启用失败：' + error.message)
      }
    },
    // 禁用
    async handleDisable(row) {
      try {
        await ElMessageBox.confirm(
          `确定要禁用任务"${row.taskName}"吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        const res = await disableScheduledTask(row.id)
        if (res.code === '000000') {
          ElMessage.success('禁用成功')
          this.loadData()
        } else {
          ElMessage.error(res.msg || '禁用失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('禁用失败：' + error.message)
        }
      }
    },
    // 执行一次
    async handleExecuteOnce(row) {
      try {
        row.executing = true
        await executeScheduledTaskOnce(row.id)
        // 如果到这里说明请求成功且业务状态码是 000000（响应拦截器已处理其他错误情况）
        ElMessage.success('执行成功')
        this.loadData()
      } catch (error) {
        // 所有错误（业务错误、网络错误等）已在响应拦截器中统一处理并弹出提示
        // 这里不需要再弹错误提示，避免重复
      } finally {
        row.executing = false
      }
    },
    // 刷新所有任务
    async handleRefreshAll() {
      try {
        const res = await refreshAllScheduledTasks()
        if (res.code === '000000') {
          ElMessage.success('刷新成功')
          this.loadData()
        } else {
          ElMessage.error(res.msg || '刷新失败')
        }
      } catch (error) {
        ElMessage.error('刷新失败：' + error.message)
      }
    },
    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.taskFormRef.validate()
        this.submitting = true
        
        let res
        if (this.taskForm.id) {
          res = await updateScheduledTask(this.taskForm)
        } else {
          res = await createScheduledTask(this.taskForm)
        }
        
        if (res.code === '000000') {
          ElMessage.success(this.taskForm.id ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.loadData()
        } else {
          ElMessage.error(res.msg || '操作失败')
        }
      } catch (error) {
        if (error !== false) {
          ElMessage.error('操作失败：' + error.message)
        }
      } finally {
        this.submitting = false
      }
    },
    // 对话框关闭
    handleDialogClose() {
      this.$refs.taskFormRef?.resetFields()
    },
    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) {
        return '-'
      }
      return dateTime.replace('T', ' ').substring(0, 19)
    },
    // 查看日志
    handleViewLogs(row) {
      this.currentTask = row
      this.logQueryParams = {
        executeStatus: null,
        startTime: null,
        endTime: null,
        current: 1,
        size: 20
      }
      this.logDialogVisible = true
      this.loadLogData()
    },
    // 加载日志数据
    async loadLogData() {
      if (!this.currentTask) {
        return
      }
      this.logLoading = true
      try {
        const res = await getScheduledTaskLogList(this.currentTask.id, this.logQueryParams)
        if (res.code === '000000') {
          this.logList = res.data.records || []
          this.logTotal = res.data.total || 0
        } else {
          ElMessage.error(res.msg || '加载日志失败')
        }
      } catch (error) {
        ElMessage.error('加载日志失败：' + error.message)
      } finally {
        this.logLoading = false
      }
    },
    // 日志搜索
    handleLogSearch() {
      this.logQueryParams.current = 1
      this.loadLogData()
    },
    // 日志重置
    handleLogReset() {
      this.logQueryParams = {
        executeStatus: null,
        startTime: null,
        endTime: null,
        current: 1,
        size: 20
      }
      this.loadLogData()
    },
    // 日志分页大小改变
    handleLogSizeChange(size) {
      this.logQueryParams.size = size
      this.logQueryParams.current = 1
      this.loadLogData()
    },
    // 日志页码改变
    handleLogPageChange(page) {
      this.logQueryParams.current = page
      this.loadLogData()
    },
    // 日志对话框关闭
    handleLogDialogClose() {
      this.currentTask = null
      this.logList = []
      this.logTotal = 0
    },
    // 查看错误堆栈
    handleViewErrorStack(row) {
      this.errorStackContent = row.errorStack || '无堆栈信息'
      this.errorStackDialogVisible = true
    }
  }
}
</script>

<style scoped>
.scheduled-task-tip {
  margin-bottom: 14px;
}

.scheduled-task-tip code {
  margin: 0 3px;
}

.task-preset-row {
  margin-bottom: 8px;
}

.task-preset-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.scheduled-task-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.5;
}

.log-dialog-content {
  padding: 10px 0;
}

.log-task-info {
  margin-bottom: 20px;
}

.log-search-form {
  margin-bottom: 20px;
}

.log-pagination {
  margin-top: 20px;
  text-align: right;
}
</style>

